import { AfterContentInit, Component, Input, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { ESRI } from '../../../esri-loader-util';
import { InputConverter } from '../../../inputConverter';
import { LayerService } from '../../../services/layer.service';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-pennbots-connector',
    styleUrls: ['../../sharedStyles/base.scss'],
    templateUrl: './pennbots-connector.component.html'
})
export class PennbotsConnectorComponent extends BaseComponent implements AfterContentInit {

    streetviewInitialized: boolean = false;
    videologInitialized: boolean = false;

    scopeDictionary: { [scopeId: number]: any[] } = {};

    angle: number = 0;
    arrowColorR: number = 40;
    arrowColorG: number = 96;
    arrowColorB: number = 144;
    arrowGraphic: __esri.Graphic;
    arrowGraphicsLayer: __esri.GraphicsLayer;
    arrowPath: string = 'M192 320L0 576h128v128h128V576h128L192 320z';
    arrowSize: string = '16px';
    arrowSymbol: any;

    videologColor: number[] = [0, 0, 0];
    videologGraphic: __esri.Graphic;
    videologSymbol: any;

    webMercatorUtils: __esri.webMercatorUtils;

    @Input() @InputConverter() lat: number;
    @Input() @InputConverter() long: number;
    @Input() @InputConverter() videologTolerance: number = 400;
    @Input() mapViewRef: NonNullable<__esri.MapView>;
    @Input() petlLayerId: number = 2701;
    @Input() @InputConverter() streetviewScopeId: number = 0;
    @Input() @InputConverter() videologScopeId: number = 0;

    @Input()
    get streetviewEnabled() { return this._streetviewEnabled; }
    set streetviewEnabled(value) {
        this._streetviewEnabled = value;
        const $scope = this.getAngularScope('streetview');
        if ($scope) {
            $scope.streetviewActive = value;
        }
        if (value) {
            if (!this.streetviewInitialized) {
                this.setupStreetviewWatch();
                this.initializeStreetViewMarker();
                this.streetviewInitialized = true;
            }
            this.setStreetviewPosition(true);
        } else {
            if (this.arrowGraphic) {
                this.mapViewRef.graphics.remove(this.arrowGraphic);
            }
        }
    }
    private _streetviewEnabled = false;

    @Input()
    get videologEnabled() { return this._videologEnabled; }
    set videologEnabled(value) {
        this._videologEnabled = value;
        const $scope = this.getAngularScope('videolog');
        if ($scope) {
            $scope.videologActive = value;
        }
        const videologScope = this.getScopeById(this.videologScopeId);
        if (videologScope) {
            videologScope.vm.options.crsTolerance = this.videologTolerance;
            if (value) {
                if (!this.videologInitialized) {
                    this.setupVideologWatch();
                    this.initializeVideologMarker();
                    this.videologInitialized = true;
                }
                this.setVideologPosition(true);

            } else {
                videologScope.$$childHead.vm.startOrStopVideo(false);
                if (this.videologGraphic) {
                    this.mapViewRef.graphics.remove(this.videologGraphic);
                }
            }
        }
    }
    private _videologEnabled = false;

    constructor(_vcr: ViewContainerRef, private readonly layerService: LayerService) {
        super(_vcr, false);
        this.initialize();
    }

    async initialize() {
        if (!window.jQuery) {
            window.$ = function() { return { draggable() { return false; } }; };
        } else if (!window.jQuery.ui) {
            window.jQuery.fn.extend({ draggable() { return false; } });
        }
        await this.loadEsriModules();
        await this.setMapViewRef();
        await this.initializeMapViewClickHandler();
    }

    initializeMapViewClickHandler() {
        this.mapViewRef.on('click', (event) => {
            if (this.streetviewEnabled || this.videologEnabled) {
                const geographicPoint = <__esri.Point>this.webMercatorUtils.webMercatorToGeographic(event.mapPoint);
                const precision = 5;
                this.lat = parseFloat(geographicPoint.y.toFixed(precision));
                this.long = parseFloat(geographicPoint.x.toFixed(precision));
            }
            if (this.streetviewEnabled) {
                this.setStreetviewPosition();
            }
            if (this.videologEnabled) {
                this.setVideologPosition();
            }
        });
    }

    initializeStreetViewMarker() {
        const streetViewArrowColor: number[] = [this.arrowColorR, this.arrowColorG, this.arrowColorB];
        const symbolType = 'simple-marker';
        this.arrowSymbol = {
            type: symbolType,
            color: streetViewArrowColor,
            size: this.arrowSize,
            path: this.arrowPath,
            angle: 0,
            outline: {
                color: streetViewArrowColor,
                width: 1
            }
        };
    }

    initializeVideologMarker() {
        const symbolType = 'simple-marker';
        this.videologSymbol = {
            type: symbolType,
            color: this.videologColor,
            size: 10,
            angle: 0,
            outline: {
                color: this.videologColor,
                width: 1
            }
        };
    }

    async setMapViewRef() {
        if (!this.mapViewRef) {
            const parent = await this.getClosestAncestor('mc-map');
            if (parent) {
                await parent.ready;
                this.mapViewRef = parent.mapView;
            } else {
                this.readyProblem('Map View Reference not supplied and reference from parent element could not be retrieved.');
            }
        }
    }

    async loadEsriModules() {
        this.webMercatorUtils = await ESRI.WebMercatorUtils;
    }

    async ngAfterContentInit() {
        const angular = (<any>window).angular;
        angular.module('PennBOTSApp', ['bots', 'petl']).config(
            ['$compileProvider', ($compileProvider) => {
                $compileProvider.preAssignBindingsEnabled(true);
            }]
        );
        angular.module('PennBOTSApp').controller('pennbots.main', ['$rootScope', 'petlService',
            ($rootScope, petlService) => { this.mainController($rootScope, petlService); }]);
    }

    mainController($rootScope, petlService) {
        $rootScope.$on(petlService.mapProjectBroadcastName, () => {
            this.addPETLProjectToMap(petlService.project.id);
        });
    }

    async setStreetviewPosition(apply = false) {
        if (this.lat && this.long) {
            const scope = this.getScopeById(this.streetviewScopeId);
            scope.isActive = true;
            scope.lat = this.lat;
            scope.long = this.long;
            scope.heading = this.angle;
            scope.vm.zoom = this.mapViewRef.zoom;
            if (apply) {
                scope.$apply();
            }
            this.setStreetviewGraphicPosition();
        }
    }

    async setVideologPosition(apply = false) {
        if (this.lat && this.long) {
            const scope = this.getScopeById(this.videologScopeId);
            scope.vm.isActive = true;
            scope.vm.lat = this.lat;
            scope.vm.long = this.long;
            if (apply) {
                scope.$apply();
            }
            this.setVideologGraphicPosition();
        }
    }

    setupStreetviewWatch() {
        const $scope = this.getAngularScope('streetview');
        const streetviewScope = this.getScopeById(this.streetviewScopeId);
        $scope.$watchGroup([() => streetviewScope.lat, () => streetviewScope.long, () => streetviewScope.heading], () => {
            if (streetviewScope && streetviewScope.long) {
                this.long = streetviewScope.long;
                this.lat = streetviewScope.lat;
                this.angle = streetviewScope.heading;
            }
            if (streetviewScope && this.streetviewEnabled) {
                this.setStreetviewPosition();
            }
        });
    }

    setupVideologWatch() {
        const $scope = this.getAngularScope('videolog');
        const videologScope = this.getScopeById(this.videologScopeId);
        $scope.$watchGroup([() => videologScope.vm.lat, () => videologScope.vm.long], () => {
            if (videologScope && videologScope.vm.long) {
                this.long = videologScope.vm.long;
                this.lat = videologScope.vm.lat;
            }
            if (videologScope && this.videologEnabled) {
                this.setVideologPosition();
            }
        });
    }

    getAngularScope(elementId: string) {
        const streetview = document.getElementById(elementId);
        const angular = (<any>window).angular;
        return angular.element(streetview).scope();
    }

    getScopeById(id: number) {
        if (this.scopeDictionary[id]) {
            return this.scopeDictionary[id];
        } else {
            const scope = this.findScopeByid(id);
            if (scope) {
                this.scopeDictionary[id] = scope;
            }
            return scope;
        }
    }

    findScopeByid(id: number) {
        const angular = (<any>window).angular;
        const ngScopeArray = [].slice.call(document.querySelectorAll('.ng-scope'));
        const elementArrray = ngScopeArray.map(x => angular.element(x));
        const scopeArray = elementArrray.map(x => x.scope());
        const scopesFound = scopeArray.filter(y => y.$id === id);
        if (scopesFound.length > 0) {
            return scopesFound[0];
        } else {
            const childHeads = scopeArray.filter(x => x.$$childHead).map(y => y.$$childHead);
            const childHeadsFound = childHeads.filter(x => x.$id === id);
            if (childHeadsFound.length > 0) {
                return childHeadsFound[0];
            } else {
                const childSiblings = childHeads.filter(x => x.$$nextSibling).map(y => y.$$nextSibling);
                const childSiblingsFound = childSiblings.filter(x => x.$id === id);
                if (childSiblingsFound.length > 0) {
                    return childSiblingsFound[0];
                }
            }
        }
        return null;
    }

    async setStreetviewGraphicPosition() {
        const [Graphic, Point] = await ESRI.loadModules('esri/Graphic', 'esri/geometry/Point');
        const geographicPoint = new Point({ x: this.long, y: this.lat });
        if (this.angle || this.angle === 0) {
            this.arrowSymbol.angle = this.angle;
        }
        if (this.arrowGraphic) {
            this.mapViewRef.graphics.remove(this.arrowGraphic);
        }
        this.arrowGraphic = new Graphic({
            geometry: geographicPoint,
            symbol: this.arrowSymbol
        });
        this.mapViewRef.graphics.add(this.arrowGraphic);
    }

    async setVideologGraphicPosition() {
        const [Graphic, Point] = await ESRI.loadModules('esri/Graphic', 'esri/geometry/Point');
        const geographicPoint = new Point({ x: this.long, y: this.lat });
        if (this.videologGraphic) {
            this.mapViewRef.graphics.remove(this.videologGraphic);
        }
        this.videologGraphic = new Graphic({
            geometry: geographicPoint,
            symbol: this.videologSymbol
        });
        this.mapViewRef.graphics.add(this.videologGraphic);
    }

    async addPETLProjectToMap(projectId: number) {
        const query = { field: 'project_no', operator: '=', value: projectId.toString() };
        query.operator = '=';
        const layerSettings = { id: this.petlLayerId, query, zoomToLayer: true };
        this.layerService.addMapcoreLayersToMap([layerSettings]);
    }
}
